import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { authApi } from '../api/client.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { FormMessage } from '../components/FormMessage.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';

type State = 'pending' | 'done' | 'failed';

export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [state, setState] = useState<State>(token ? 'pending' : 'failed');
  /** Токен одноразовый: в StrictMode эффект вызывается дважды, второй вызов сжёг бы его. */
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    void authApi
      .verifyEmail(token)
      .then((response) => {
        /**
         * Подтверждение почты не выдаёт сессию: по ссылке из письма может прийти
         * кто угодно и в любом браузере. Писать пользователя в контекст можно
         * только когда это тот же человек, что уже вошёл, — иначе `RequireAuth`
         * пустил бы в `/app` без access-токена, а при живой чужой сессии
         * интерфейс показывал бы одного пользователя, а запросы шли от другого.
         */
        if (user && user.id === response.user.id) setUser(response.user);
        setState('done');
      })
      .catch(() => {
        setState('failed');
      });
  }, [token, user, setUser]);

  // A used link from an address that is already confirmed is not a failure for the person
  // holding it: they are simply done. Only this browser's session can tell, so only it says so.
  const alreadyConfirmed = state === 'failed' && Boolean(user?.emailVerified);
  const next = user ? (
    <Link to="/app" className="kitup-primary-button w-full justify-center">
      {t.auth.verify.toApp}
    </Link>
  ) : (
    <Link to="/login" className="kitup-primary-button w-full justify-center">
      {t.auth.verify.toSignIn}
    </Link>
  );

  return (
    // Once confirmed, the heading says so: "Confirm your email" above "Email confirmed"
    // read as an instruction still to follow.
    <AuthLayout
      title={state === 'done' || alreadyConfirmed ? t.auth.verify.titleDone : t.auth.verify.title}
    >
      {state === 'pending' ? <p role="status">{t.auth.verify.pending}</p> : null}
      {state === 'done' ? (
        <div className="grid gap-4">
          <FormMessage kind="success">{t.auth.verify.done}</FormMessage>
          {next}
        </div>
      ) : null}
      {alreadyConfirmed ? (
        <div className="grid gap-4">
          <FormMessage kind="success">{t.auth.verify.alreadyConfirmed}</FormMessage>
          {next}
        </div>
      ) : null}
      {state === 'failed' && !alreadyConfirmed ? (
        <div className="grid gap-4">
          <FormMessage kind="error">{t.auth.verify.failed}</FormMessage>
          {next}
        </div>
      ) : null}
    </AuthLayout>
  );
}
